import { ChevronLeft } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { NAV_SECTIONS, type NavItemDef } from '@/config/nav';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

function isActive(item: NavItemDef, pathname: string): boolean {
  const prefixes = item.match ?? [item.path];
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <aside
      className={cn(
        'flex flex-none flex-col border-r border-c-line bg-c-bg text-c-mid transition-[width] [transition-duration:160ms] ease-out',
        collapsed ? 'w-[72px]' : 'w-[238px]',
      )}
    >
      {/* BRAND ROW */}
      <div
        className={cn(
          'flex items-center gap-[10px] border-b border-c-line p-[16px]',
          collapsed && 'justify-center',
        )}
      >
        <button
          type="button"
          title="Menú"
          onClick={onToggle}
          className="grid h-[32px] w-[32px] flex-none place-items-center rounded-[8px] bg-c-accent font-mono text-[10.5px] font-extrabold tracking-[.3px] text-[#062018]"
        >
          C4T
        </button>
        {!collapsed && (
          <>
            <div className="min-w-0 leading-[1.25]">
              <div className="whitespace-nowrap text-[12.5px] font-extrabold tracking-[1px] text-c-brand">
                C4T AMBULANCIAS
              </div>
              <div className="mt-[2px] whitespace-nowrap font-mono text-[8.5px] tracking-[.5px] text-c-dim">
                AYTO. POZUELO · SEAPA
              </div>
            </div>
            <button
              type="button"
              title="Plegar menú"
              onClick={onToggle}
              className="ml-auto grid h-[26px] w-[26px] flex-none place-items-center rounded-[7px] text-c-dim transition-colors hover:bg-c-hover-bg hover:text-c-text"
            >
              <ChevronLeft size={16} strokeWidth={2} />
            </button>
          </>
        )}
      </div>

      {/* NAV */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-[6px_0_8px]">
        {NAV_SECTIONS.map((section) => (
          <div key={section.header}>
            {!collapsed && (
              <div className="px-[18px] pb-[5px] pt-[14px] font-mono text-[8.5px] uppercase tracking-[1.6px] text-c-dim">
                {section.header}
              </div>
            )}
            {collapsed && <div className="pt-[10px]" />}
            {section.items.map((item) => {
              const active = isActive(item, pathname);
              const Icon = item.icon;
              const node = (
                <button
                  type="button"
                  key={item.key}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    'flex w-auto cursor-pointer items-center gap-[11px] rounded-[8px] text-[13px] font-medium transition-[background,color] [transition-duration:120ms]',
                    collapsed
                      ? 'mx-[12px] my-[2px] justify-center px-0 py-[11px]'
                      : 'mx-[12px] my-[1px] px-[12px] py-[9px]',
                    active
                      ? 'bg-c-active-bg font-semibold text-c-active-ink'
                      : 'text-c-mid hover:bg-c-hover-bg hover:text-c-text',
                  )}
                  style={
                    active
                      ? { boxShadow: 'inset 2px 0 0 var(--c-accent)' }
                      : undefined
                  }
                >
                  <Icon size={17} strokeWidth={1.8} className="flex-none" />
                  {!collapsed && (
                    <>
                      <span className="whitespace-nowrap">{item.label}</span>
                      {item.badge?.kind === 'dot' && (
                        <span
                          className="ml-auto h-[7px] w-[7px] flex-none rounded-full"
                          style={{ background: item.badge.color }}
                        />
                      )}
                      {item.badge?.kind === 'pill' && (
                        <span
                          className={cn(
                            'ml-auto rounded-[9px] px-[6px] py-[1px] font-mono text-[9px]',
                            item.badge.className,
                          )}
                        >
                          {item.badge.text}
                        </span>
                      )}
                    </>
                  )}
                </button>
              );

              if (!collapsed) return node;
              return (
                <Tooltip key={item.key} delayDuration={200}>
                  <TooltipTrigger asChild>{node}</TooltipTrigger>
                  <TooltipContent side="right">{item.label}</TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        ))}
      </nav>

      {/* FOOTER */}
      <div
        className={cn(
          'flex items-center gap-[9px] border-t border-c-line p-[12px_16px] text-[11px] text-c-mid',
          collapsed && 'justify-center',
        )}
      >
        <span className="h-[26px] w-[26px] flex-none rounded-[6px] bg-[linear-gradient(135deg,#1fd39a,#3aa0ff)]" />
        {!collapsed && (
          <span className="whitespace-nowrap">Coordinador · turno M</span>
        )}
      </div>
    </aside>
  );
}
